import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { AbstractTile } from "@/components/design/AbstractTile";
import { BackButton } from "@/components/common/BackButton";
import { FilterChip } from "@/components/design/FilterChip";
import { MEDIA_KIND_LABEL, type MediaKind } from "@/components/grow/media-kind";
import { GrowWatchDocument } from "@/graphql/generated/operations";
import type { GrowWatchQuery } from "@/graphql/generated/operations";
import { ELEMENT_CODES, ELEMENT_GROUPS, GROUP_ORDER } from "@/lib/elements";
import { POWER_BY_ABBR } from "@/lib/power-library";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";

// Grow · Watch (workshop snapshot screens/GrowWatch.jsx).
// Catalogue of the consumable learning content - videos, podcasts and scripts -
// across all powers. Wired to genesis' growResource (aggregated per element in
// the GrowWatch query). The resource tables are still empty, so the empty feed
// is the expected normal case for now.

type WatchKind = MediaKind;
type TileVariant = "lines" | "rings" | "grid";

interface WatchItem {
  id: string;
  kind: WatchKind;
  title: string;
  power: string;
  group: string;
  durationMs?: number | null;
  thumbnail?: string | null;
  hue: number;
  variant: TileVariant;
}

const KIND_STYLE: Record<WatchKind, { hue: number; variant: TileVariant }> = {
  Video: { hue: 210, variant: "lines" },
  Podcast: { hue: 280, variant: "rings" },
  Script: { hue: 40, variant: "grid" },
};

// All 30 powers in canonical group order; the GrowWatch query aliases one
// growResource call per element, so we read the result back by element key.
const ALL_ELEMENTS: string[] = GROUP_ORDER.flatMap((g) => ELEMENT_GROUPS[g].elements);

const POWER_BY_ELEMENT = new Map(
  ALL_ELEMENTS.map((el) => {
    const code = ELEMENT_CODES[el];
    return [el, code ? POWER_BY_ABBR[code] : undefined] as const;
  }),
);

function groupLabel(element: string, intl: IntlShape): string {
  for (const g of GROUP_ORDER) {
    if (ELEMENT_GROUPS[g].elements.includes(element)) {
      return intl.formatMessage({ id: ELEMENT_GROUPS[g].labelId });
    }
  }
  return "";
}

function flattenWatchItems(data: GrowWatchQuery | undefined, intl: IntlShape): WatchItem[] {
  if (!data) return [];
  const byElement = data as unknown as Record<string, GrowWatchQuery["PERSEVERANCE"]>;
  const items: WatchItem[] = [];
  for (const element of ALL_ELEMENTS) {
    const resource = byElement[element];
    if (!resource) continue;
    const power = POWER_BY_ELEMENT.get(element);
    const powerName = power ? intl.formatMessage({ id: power.nameId }) : element;
    const group = groupLabel(element, intl);
    for (const [i, v] of (resource.videos ?? []).entries()) {
      items.push({
        id: `${element}-video-${i}`,
        kind: "Video",
        title: v.title.localized ?? powerName,
        power: powerName,
        group,
        durationMs: v.lengthMilliseconds,
        thumbnail: v.thumbnail,
        ...KIND_STYLE.Video,
      });
    }
    for (const [i, a] of (resource.audio ?? []).entries()) {
      items.push({
        id: `${element}-audio-${i}`,
        kind: "Podcast",
        title: a.title?.localized ?? powerName,
        power: powerName,
        group,
        durationMs: a.lengthMilliseconds,
        thumbnail: a.thumbnail,
        ...KIND_STYLE.Podcast,
      });
    }
    for (const [i, t] of (resource.text ?? []).entries()) {
      items.push({
        id: `${element}-text-${i}`,
        kind: "Script",
        title: t.title?.localized ?? powerName,
        power: powerName,
        group,
        ...KIND_STYLE.Script,
      });
    }
  }
  return items;
}

function formatDuration(ms?: number | null): string | null {
  if (!ms || ms <= 0) return null;
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")} Min`;
}

type WatchFilter = "Alle" | WatchKind;

const FILTERS: readonly WatchFilter[] = ["Alle", "Video", "Podcast", "Script"];

export function GrowWatchPage() {
  const intl = useIntl();
  const [filter, setFilter] = useState<WatchFilter>("Alle");
  const { data, loading } = useQuery(GrowWatchDocument);

  const filterLabels: Record<WatchFilter, string> = {
    Alle: intl.formatMessage({ id: "grow.watch.filterAll", defaultMessage: "All" }),
    Video: intl.formatMessage(MEDIA_KIND_LABEL.Video),
    Podcast: intl.formatMessage(MEDIA_KIND_LABEL.Podcast),
    Script: intl.formatMessage(MEDIA_KIND_LABEL.Script),
  };

  const items = flattenWatchItems(data, intl);
  const filtered = filter === "Alle" ? items : items.filter((i) => i.kind === filter);

  // Live assistant context (motive 1, ADR 0020). The resource tables are usually empty, so this
  // lets the assistant say so honestly rather than guess.
  useAssistantContext(
    data
      ? [
          {
            description: "learning content on the Watch page",
            value:
              items.length === 0
                ? "no learning content available yet (videos/podcasts/scripts tables are empty)"
                : `${items.length} items` + (filter !== "Alle" ? `, filtered to ${filter}` : ""),
          },
        ]
      : null,
  );

  // Guided tour (/tour): the Watch catalogue — filter chips + content region. The resource
  // tables are usually still empty, so the content stop says so honestly.
  useAssistantTour(
    data
      ? {
          label: intl.formatMessage({
            id: "grow.watch.tour.label",
            defaultMessage: "Grow Watch",
          }),
          summary:
            items.length === 0
              ? intl.formatMessage({
                  id: "grow.watch.tour.emptySummary",
                  defaultMessage:
                    "This is Watch: learning content around your powers as videos, podcasts and scripts. Right now there is nothing here yet.",
                })
              : intl.formatMessage(
                  {
                    id: "grow.watch.tour.summary",
                    defaultMessage:
                      "This is Watch: learning content around your powers as videos, podcasts and scripts. Right now there are {count} items here.",
                  },
                  { count: items.length },
                ),
          stops: [
            {
              anchor: "grow.watch.filters",
              title: intl.formatMessage({
                id: "grow.watch.tour.filters.title",
                defaultMessage: "Content filter",
              }),
              description: intl.formatMessage({
                id: "grow.watch.tour.filters.desc",
                defaultMessage:
                  "Narrow the catalogue by format (videos, podcasts or scripts), or pick All for everything.",
              }),
            },
            {
              anchor: "grow.watch.content",
              title: intl.formatMessage({
                id: "grow.watch.tour.content.title",
                defaultMessage: "The catalogue",
              }),
              description:
                items.length === 0
                  ? intl.formatMessage({
                      id: "grow.watch.tour.content.emptyDesc",
                      defaultMessage:
                        "No content is available yet. As soon as videos, podcasts or scripts exist, they will appear here as cards you can open.",
                    })
                  : intl.formatMessage(
                      {
                        id: "grow.watch.tour.content.desc",
                        defaultMessage:
                          "{count} content items, each tagged with its power and cluster. Click a card to consume it.",
                      },
                      { count: items.length },
                    ),
            },
          ],
        }
      : null,
  );

  return (
    <div className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <div className="mb-5 flex items-baseline justify-between">
        <BackButton />
        <div className="text-[10px] tracking-[0.25em] text-ink/55">
          <FormattedMessage
            id="grow.watch.contentCount"
            defaultMessage="{count} content items"
            values={{ count: items.length }}
          />
        </div>
      </div>

      <h1 className="font-serif text-5xl leading-tight">
        <FormattedMessage id="grow.watch.title" defaultMessage="Watch" />
      </h1>
      <p className="mt-3 max-w-xl text-sm text-ink/55">
        <FormattedMessage
          id="grow.watch.intro"
          defaultMessage="Content to consume around your powers: videos, podcasts and scripts. Sit down, watch, listen and read up."
        />
      </p>

      <div className="mt-6 flex flex-wrap gap-2" data-assistant-anchor="grow.watch.filters">
        {FILTERS.map((f) => (
          <FilterChip key={f} active={f === filter} onClick={() => setFilter(f)}>
            {filterLabels[f]}
          </FilterChip>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div
          className="mt-8 rounded-md bg-ink/[0.02] px-4 py-10 text-center ring-1 ring-ink/15"
          data-assistant-anchor="grow.watch.content"
        >
          <div className="text-sm text-ink/55">
            {loading ? (
              <FormattedMessage id="grow.watch.loadingContent" defaultMessage="Content is loading …" />
            ) : (
              <FormattedMessage
                id="grow.watch.emptyContent"
                defaultMessage="No content available yet. As soon as videos, podcasts or scripts exist, they will appear here."
              />
            )}
          </div>
        </div>
      ) : (
        <div
          className="mt-6 grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-3 @xl:grid-cols-4"
          data-assistant-anchor="grow.watch.content"
        >
          {filtered.map((item) => (
            <WatchCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function WatchCard({ item }: { item: WatchItem }) {
  const intl = useIntl();
  const duration = formatDuration(item.durationMs);
  return (
    <button className="overflow-hidden rounded-md bg-surface text-left ring-1 ring-ink/5 transition hover:ring-accent/40">
      <div className="relative">
        {item.thumbnail ? (
          <img
            src={`data:image/jpeg;base64,${item.thumbnail}`}
            alt={item.title}
            className="aspect-[16/10] w-full object-cover"
          />
        ) : (
          <AbstractTile variant={item.variant} hue={item.hue} className="aspect-[16/10]" />
        )}
        <span className="absolute left-2 top-2 rounded bg-ink px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-on-ink">
          {intl.formatMessage(MEDIA_KIND_LABEL[item.kind])}
        </span>
        {duration ? (
          <span className="absolute bottom-2 right-2 rounded bg-ink/80 px-1.5 py-0.5 text-[9px] tracking-widest text-on-ink">
            {duration}
          </span>
        ) : null}
      </div>
      <div className="px-4 py-3">
        <div className="text-sm font-medium leading-tight">{item.title}</div>
        <div className="mt-3 flex items-center justify-between text-[10px] tracking-wider text-ink/40">
          <span>{item.power}</span>
          <span>{item.group}</span>
        </div>
      </div>
    </button>
  );
}
